import { Link, createFileRoute, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight } from 'lucide-react'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { localizedPageHead } from '@/lib/seo-head'
import { HomeEvidence } from '@/components/ledger/HomeEvidence'
import {
  LedgerCta,
  LedgerRow,
  LedgerSection,
  LedgerStamp,
} from '@/components/ledger/LedgerBlocks'

export const Route = createFileRoute('/{-$locale}/')({
  head: ({ params }) => localizedPageHead('home', params.locale),
  component: LedgerHome,
})

const OFFER = [
  { key: 'engineering', href: '/what-we-do' },
  { key: 'cloud', href: '/cloud' },
  { key: 'products', href: '/products' },
  { key: 'agents', href: '/work-with-us' },
  { key: 'ai', href: '/ai' },
  { key: 'research', href: '/research' },
] as const

function LedgerHome() {
  const { t } = useTranslation('ledger')
  const locale = getLocaleFromPath(useLocation().pathname)
  const href = (path: string) => withLocalePath(path, locale)

  return (
    <div>
      {/* First viewport: the register's document head. */}
      <section className="ledger-wrap pb-12 pt-8 md:pt-10">
        <div className="ledger-masthead">
          <span className="ledger-masthead-brand">{t('masthead.legal')}</span>
          <span className="ledger-masthead-note">{t('masthead.hosting')}</span>
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <h1 className="ledger-display text-[clamp(2.6rem,7vw,5.2rem)]">
              {t('home.headline')}
              <br />
              <em style={{ fontStyle: 'italic' }}>{t('home.headlineEnd')}</em>
            </h1>
            <p className="ledger-lead mt-7">{t('home.lead')}</p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <Link to={href('/what-we-do')} className="ledger-button ledger-focus">
                {t('home.primaryCta')}
              </Link>
              <a href={href('/community')} className="ledger-link">{t('home.communityCta')}</a>
            </div>
          </div>

          <aside className="lg:col-span-4 lg:pt-2">
            <p className="ledger-kicker">{t('home.recordTitle')}</p>
            <div className="mt-4 border-t border-[var(--rule-strong)]">
              <p className="ledger-serif border-b border-[var(--rule)] py-4 text-xl">Horizon Tech d.o.o.</p>
              <p className="ledger-muted border-b border-[var(--rule)] py-3">{t('home.recordLocation')}</p>
              <p className="ledger-muted border-b border-[var(--rule)] py-3">{t('home.recordScope')}</p>
            </div>
            <p className="ledger-muted mt-4 text-[0.875rem] leading-relaxed">{t('home.recordBody')}</p>
            <Link to={href('/about')} className="ledger-link mt-5 inline-flex">
              {t('home.companyLink')}<ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </aside>
        </div>
      </section>

      {/* The offer, as ruled register entries. */}
      <LedgerSection title={t('home.offerTitle')} note={t('home.offerIntro')}>
        <div className="ledger-register">
          {OFFER.map((item, i) => (
            <LedgerRow
              key={item.key}
              index={String(i + 1).padStart(2, '0')}
              name={t(`home.offer.${item.key}.name`)}
              desc={t(`home.offer.${item.key}.desc`)}
              tag={t(`home.offer.${item.key}.tag`)}
              href={item.href}
            />
          ))}
        </div>
      </LedgerSection>

      {/* The flagship, given its own notice. */}
      <section className="ledger-wrap ledger-section">
        <div className="ledger-section-head">
          <h2 className="ledger-section-title">Horizon Cloud</h2>
          <LedgerStamp>{t('home.cloudStatus')}</LedgerStamp>
        </div>
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="ledger-serif text-[clamp(1.15rem,2vw,1.5rem)] leading-snug text-[var(--ink)]">
              {t('home.cloudBody')}
            </p>
            <Link to={href('/cloud')} className="ledger-link mt-6 inline-flex">
              {t('home.cloudLink')}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </div>
          <ul className="md:col-span-5">
            {(
              ['databases', 'containers', 'apps', 'storage', 'inference'] as const
            ).map((key) => (
              <li
                key={key}
                className="ledger-serif border-b border-[var(--rule)] py-2.5 text-[1.05rem] text-[var(--ink)]"
              >
                {t(`home.cloudServices.${key}`)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Three messaging pillars. */}
      <LedgerSection title={t('home.pillarsTitle')}>
        <div className="ledger-pillars">
          {(['sovereignty', 'managed', 'agents'] as const).map((key, i) => (
            <article key={key} className="ledger-pillar">
              <span className="ledger-pillar-no">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="ledger-pillar-title">
                {t(`home.pillars.${key}.title`)}
              </h3>
              <p className="ledger-pillar-body">
                {t(`home.pillars.${key}.body`)}
              </p>
            </article>
          ))}
        </div>
      </LedgerSection>

      {/* Audience, in the order we win them. */}
      <LedgerSection title={t('home.audienceTitle')} note={t('home.audienceIntro')}>
        <div className="ledger-register">
          {(['smb', 'regulated', 'partners'] as const).map((key, i) => (
            <LedgerRow
              key={key}
              index={String(i + 1).padStart(2, '0')}
              name={t(`home.audiences.${key}.name`)}
              desc={t(`home.audiences.${key}.body`)}
            />
          ))}
        </div>
      </LedgerSection>

      {/* Own software, run on the platform. */}
      <LedgerSection
        title={t('home.productsTitle')}
        note={t('home.productsIntro')}
      >
        <div className="ledger-register">
          <LedgerRow
            index="01"
            name="arkived"
            desc={t('home.arkived')}
            tag="Open source"
            href="/products/arkived"
          />
        </div>
      </LedgerSection>

      {/* Research. */}
      <LedgerSection title={t('home.researchTitle')}>
        <p className="ledger-serif max-w-[62ch] text-[clamp(1.15rem,2vw,1.5rem)] leading-snug">
          {t('home.researchBody')}
        </p>
        <Link to={href('/research')} className="ledger-link mt-6 inline-flex">
          {t('home.researchLink')}
          <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
      </LedgerSection>

      <HomeEvidence />

      {/* Values, as a four-up ruled strip — a different register from the tables. */}
      <LedgerSection title={t('home.valuesTitle')}>
        <div className="grid gap-x-8 gap-y-6 border-t border-[var(--rule-strong)] pt-6 sm:grid-cols-2 lg:grid-cols-4">
          {(['sovereignty', 'transparency', 'openness', 'depth'] as const).map(
            (key) => (
              <div key={key}>
                <h3 className="ledger-serif text-[1.15rem] font-semibold text-[var(--ink)]">
                  {t(`home.values.${key}.name`)}
                </h3>
                <p className="ledger-muted mt-2 text-[0.875rem] leading-relaxed">
                  {t(`home.values.${key}.body`)}
                </p>
              </div>
            ),
          )}
        </div>
      </LedgerSection>

      <LedgerCta
        title={t('home.closeTitle')}
        body={t('home.closeBody')}
        ctaLabel={t('home.closeCta')}
      />
    </div>
  )
}
